import { cn } from '@/lib/utils'
import { TrendingUp, TrendingDown, Minus, AlertTriangle, ShieldCheck, Shield, ShieldAlert } from 'lucide-react'

interface RiskScoreCardProps {
  score: number
  maxScore?: number
  riskCategory: string
  title?: string
  className?: string
  showTrend?: boolean
  trendDirection?: 'up' | 'down' | 'stable'
}

export function RiskScoreCard({
  score,
  maxScore = 5,
  riskCategory,
  title = 'Final Risk Score',
  className,
  showTrend = false,
  trendDirection = 'stable',
}: RiskScoreCardProps) {
  // Determine color and icon based on score (lower is better for risk)
  const getScoreConfig = (score: number) => {
    const ratio = score / maxScore
    if (ratio <= 0.3) {
      return {
        color: 'text-risk-excellent',
        bgColor: 'bg-risk-excellent/10',
        borderColor: 'border-risk-excellent',
        label: 'Excellent',
        Icon: ShieldCheck,
      }
    }
    if (ratio <= 0.5) {
      return {
        color: 'text-risk-good',
        bgColor: 'bg-risk-good/10',
        borderColor: 'border-risk-good',
        label: 'Good',
        Icon: Shield,
      }
    }
    if (ratio <= 0.7) {
      return {
        color: 'text-risk-fair',
        bgColor: 'bg-risk-fair/10',
        borderColor: 'border-risk-fair',
        label: 'Fair',
        Icon: ShieldAlert,
      }
    }
    return {
      color: 'text-risk-poor',
      bgColor: 'bg-risk-poor/10',
      borderColor: 'border-risk-poor',
      label: 'High Risk',
      Icon: AlertTriangle,
    }
  }

  const config = getScoreConfig(score)
  const TrendIcon = trendDirection === 'up' ? TrendingUp : trendDirection === 'down' ? TrendingDown : Minus

  return (
    <div className={cn(
      'bg-white rounded-xl border shadow-sm p-5 flex flex-col',
      className
    )}>
      <h4 className="text-sm font-semibold text-gray-600 mb-4">{title}</h4>
      
      <div className="flex-1 flex flex-col items-center justify-center">
        {/* Large Score Display */}
        <div className={cn(
          'relative w-32 h-32 rounded-full flex items-center justify-center border-4',
          config.bgColor,
          config.borderColor
        )}>
          <div className="text-center">
            <span className={cn('text-4xl font-bold', config.color)}>
              {maxScore === 10 ? Math.round(score) : score.toFixed(2)}
            </span>
            <p className="text-xs text-gray-400 mt-0.5">/ {maxScore}</p>
          </div>
        </div>

        {/* Risk Category Badge */}
        <div className={cn(
          'mt-4 px-4 py-2 rounded-full flex items-center gap-2',
          config.bgColor
        )}>
          <config.Icon className={cn('h-4 w-4', config.color)} />
          <span className={cn('text-sm font-semibold', config.color)}>
            {riskCategory}
          </span>
        </div>

        {/* Optional Trend */}
        {showTrend && (
          <div className="mt-3 flex items-center gap-1 text-xs text-gray-500">
            <TrendIcon className="h-3 w-3" />
            <span>vs last analysis</span>
          </div>
        )}
      </div>

      {/* Score Scale */}
      <div className="mt-4 pt-4 border-t">
        <div className="flex justify-between text-[10px] text-gray-400 mb-1">
          <span>Low Risk</span>
          <span>High Risk</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex">
          <div className="w-[30%] bg-risk-excellent" />
          <div className="w-[20%] bg-risk-good" />
          <div className="w-[20%] bg-risk-fair" />
          <div className="w-[30%] bg-risk-poor" />
        </div>
        {/* Score Indicator */}
        <div className="relative h-3">
          <div 
            className="absolute top-0 w-0 h-0 border-l-[6px] border-r-[6px] border-b-[8px] border-l-transparent border-r-transparent border-b-gray-800 -translate-x-1/2"
            style={{ left: `${Math.min((score / maxScore) * 100, 100)}%` }}
          />
        </div>
      </div>
    </div>
  )
}
